/**
 * Created by neo on 23.12.22.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import styled from '@emotion/styled';
import { Theme } from '../../../Theme/Theme';
import { MarkdownRenderer } from '../../Markdown/MarkdownRenderer';
import { ActivityWorkoutEntryUnion } from '../../../Model/Explore/ActivityWorkoutEntry';
import { MeditationEntry } from '../../../Model/Explore/MeditationEntry';
import { ScheduleButton } from '../../ScheduleButton';
import { ShareButton } from '../../ShareButton';
import { ScheduleModal } from '../../ScheduleModal/ScheduleModal';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { EventEntry, EventEntryApi } from '../../../Model/Coach/Schedule/EventEntry';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 2vw, 24px);
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 1.5vw, 16px);
  align-items: flex-start;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const GradientLine = styled.div`
  width: min(391px, 90%);
  height: 4px;
  border-radius: 9999px;
  background: ${Theme.Colors.primary};
`;

const Description = styled.div`
  font-size: clamp(15px, 1.2vw, 17px);
  line-height: 1.7;
  color: #3c3c3c;
`;

const QuoteBlock = styled.div`
  padding: 16px 20px;
  border-left: 4px solid ${Theme.Colors.primary};
  background: ${Theme.Colors.successOpacity};
  border-radius: 0 8px 8px 0;
  font-style: italic;
  color: #3c3c3c;
`;

const ActionSection = styled.div`
  padding-top: 8px;
`;

export type ExploreContentViewActivityWorkoutContentProps = {
  entry: ActivityWorkoutEntryUnion;
};

export const ExploreContentViewActivityWorkoutContent: React.FC<ExploreContentViewActivityWorkoutContentProps> =
  observer(({ entry }) => {
    const { language } = useRootStore();
    const title = useLocalized(entry.name);
    const content = useLocalized(entry.description);

    const [showSchedule, setShowSchedule] = React.useState(false);
    const [eventEntry, setEventEntry] = React.useState<EventEntry | undefined>(undefined);

    const loadEvent = React.useCallback(() => {
      EventEntryApi.findParent({ objectType: entry.type, objectId: entry.id }).then((e) => setEventEntry(e));
    }, [entry.type, entry.id]);

    React.useEffect(() => {
      loadEvent();
    }, [loadEvent]);

    const handleShowScheduleModal = React.useCallback(() => setShowSchedule(true), []);
    const handleCloseScheduleModal = React.useCallback(() => {
      setShowSchedule(false);
      loadEvent();
    }, [loadEvent]);

    const quote = useLocalized(entry.quotes);

    useContentMetaTags({ title, description: content });

    return (
      <ContentContainer>
        <TitleContainer>
          <Title>{title}</Title>
          <GradientLine />
        </TitleContainer>
        <Description>
          <MarkdownRenderer text={content} />
        </Description>
        {entry.type === 'meditation' && quote ? (
          <QuoteBlock>
            <MarkdownRenderer text={quote} />
          </QuoteBlock>
        ) : null}
        <ActionSection>
          <ShareButton data={{ title, description: content, contentType: entry.type, contentId: entry.id }} />
          <ScheduleButton event={eventEntry} onClick={handleShowScheduleModal} />
        </ActionSection>
        {showSchedule && (
          <ScheduleModal
            key={eventEntry?.id ?? entry.id}
            exploreEntry={entry}
            parentEvent={eventEntry}
            currentEvent={eventEntry}
            disableRecurring={false}
            onSchedule={handleCloseScheduleModal}
            onDelete={handleCloseScheduleModal}
          />
        )}
      </ContentContainer>
    );
  });
